// Admin-side Virtual Meeting panel.
// Shows the current booking, lets the CA confirm attendance (→ advances step)
// or mark a no-show. History includes prior cancellations for audit.

import { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  Check,
  ExternalLink,
  History,
  Loader2,
  MessageSquareWarning,
  RefreshCw,
  Video,
  X,
} from "lucide-react";
import API from "../../lib/api";

const STATUS_PILL = {
  scheduled:  "bg-amber-100 text-amber-700",
  attended:   "bg-green-100 text-green-700",
  cancelled:  "bg-gray-100 text-gray-600",
  no_show:    "bg-red-100 text-red-600",
};

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    weekday: "short", day: "numeric", month: "short",
    hour: "numeric", minute: "2-digit",
  });
}

export default function MeetingBookingPanel({ orderId, stepNumber, stepName, onRefresh }) {
  const [data, setData] = useState({ current: null, history: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null); // 'confirm' | 'no_show'
  const [showNoShow, setShowNoShow] = useState(false);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await API.get(
        `/admin/orders/${orderId}/steps/${stepNumber}/meeting`
      );
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load meeting");
    }
  }, [orderId, stepNumber]);

  useEffect(() => { (async () => { setLoading(true); await load(); setLoading(false); })(); }, [load]);

  const onConfirm = async () => {
    if (busy) return;
    setBusy("confirm");
    try {
      await API.put(`/admin/orders/${orderId}/steps/${stepNumber}/meeting/confirm`);
      await load();
      if (onRefresh) await onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Confirm failed");
    }
    setBusy(null);
  };

  const onNoShow = async () => {
    if (busy) return;
    setBusy("no_show");
    try {
      await API.put(
        `/admin/orders/${orderId}/steps/${stepNumber}/meeting/no-show`,
        { reason: reason.trim() || null }
      );
      await load();
      setShowNoShow(false);
      setReason("");
    } catch (e) {
      alert(e.response?.data?.detail || "Update failed");
    }
    setBusy(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-[#6C63FF]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-4 text-center bg-red-50 border border-red-200 rounded-xl">
        <AlertCircle className="h-4 w-4 text-red-500 mx-auto mb-1" />
        <p className="text-xs text-red-600 font-body">{error}</p>
        <button onClick={load} className="mt-2 text-xs text-[#6C63FF] underline">Retry</button>
      </div>
    );
  }

  const cur = data.current;
  const past = data.history.filter((h) => !cur || h.id !== cur.id);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Video className="h-4 w-4 text-[#6C63FF]" />
        <h4 className="text-sm font-heading font-bold text-[#1A1A2E]">
          {stepName} — Booking
        </h4>
        <button
          onClick={load}
          className="ml-auto flex items-center gap-1 text-[11px] font-body text-[#6B7280] hover:text-[#6C63FF]"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {!cur || cur.status === "cancelled" ? (
        <div className="bg-white border border-[#E8E6F8] rounded-2xl p-4 text-center">
          <p className="text-sm text-[#6B7280] font-body">
            No active meeting booked. The client will pick a slot from your availability.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#E8E6F8] rounded-2xl p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div>
              <span className={`text-[10px] font-body font-semibold px-2 py-0.5 rounded-full ${STATUS_PILL[cur.status]}`}>
                {cur.status.replace("_", " ")}
              </span>
              <p className="text-base font-heading font-bold text-[#1A1A2E] mt-1.5">
                {fmt(cur.slot_start)} — {fmt(cur.slot_end)}
              </p>
              <p className="text-[11px] text-[#9CA3AF] font-body">
                Booked {fmt(cur.created_at)}
              </p>
            </div>
            {cur.meet_link && (
              <a
                href={cur.meet_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-body font-semibold bg-[#6C63FF] hover:bg-[#5B52CC] text-white rounded-full px-3 py-1.5"
              >
                <ExternalLink className="h-3 w-3" /> Join Meet
              </a>
            )}
          </div>

          {cur.notes && (
            <div className="bg-[#FAFAFE] border border-[#E8E6F8] rounded-lg px-3 py-2 mb-3">
              <p className="text-[10px] font-heading font-bold uppercase tracking-wider text-[#6B7280] mb-1">
                Client notes
              </p>
              <p className="text-xs text-[#1A1A2E] font-body italic">"{cur.notes}"</p>
            </div>
          )}

          {cur.status === "scheduled" && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E8E6F8]">
              <button
                onClick={onConfirm}
                disabled={busy === "confirm"}
                className="inline-flex items-center gap-1.5 text-xs font-body font-semibold bg-green-500 hover:bg-green-600 text-white rounded-lg px-3 py-1.5 disabled:opacity-60"
              >
                <Check className="h-3.5 w-3.5" />
                {busy === "confirm" ? "Confirming…" : "Mark Attended → Advance Step"}
              </button>
              <button
                onClick={() => setShowNoShow(true)}
                className="inline-flex items-center gap-1.5 text-xs font-body text-[#E57373] border border-[#E57373]/40 hover:bg-[#E57373]/10 rounded-lg px-3 py-1.5"
              >
                <X className="h-3.5 w-3.5" /> Mark No-Show
              </button>
            </div>
          )}
        </div>
      )}

      {past.length > 0 && (
        <details className="bg-white border border-[#E8E6F8] rounded-2xl">
          <summary className="cursor-pointer px-3 py-2 text-[11px] font-heading font-bold uppercase tracking-wider text-[#6B7280] flex items-center gap-1.5">
            <History className="h-3 w-3" /> Past Bookings ({past.length})
          </summary>
          <div className="border-t border-[#E8E6F8] divide-y divide-[#E8E6F8]">
            {past.map((b) => (
              <div key={b.id} className="px-3 py-2 flex items-center gap-3 flex-wrap text-xs font-body">
                <span className={`text-[10px] font-body font-semibold px-2 py-0.5 rounded-full ${STATUS_PILL[b.status]}`}>
                  {b.status.replace("_", " ")}
                </span>
                <span className="text-[#4A4A60] flex-1 truncate">
                  {fmt(b.slot_start)} – {fmt(b.slot_end)}
                </span>
                {b.cancelled_reason && (
                  <span className="text-[10px] text-[#9CA3AF] italic">"{b.cancelled_reason}"</span>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {showNoShow && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowNoShow(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-lg font-heading font-bold text-[#1A1A2E] mb-1">Mark as No-Show?</h4>
            <p className="text-sm text-[#6B7280] font-body mb-3">
              This flags the booking as missed. The client can book a fresh slot.
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              rows={3}
              className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg p-2.5 bg-[#F5F4FB] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50 mb-4 resize-none"
              placeholder="Optional reason"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowNoShow(false)}
                className="text-xs font-body border border-[#E8E6F8] rounded-full px-4 py-1.5"
              >
                Cancel
              </button>
              <button
                onClick={onNoShow}
                disabled={busy === "no_show"}
                className="text-xs font-body bg-[#E57373] hover:bg-[#d65b5b] text-white rounded-full px-4 py-1.5"
              >
                {busy === "no_show" ? "Saving…" : "Yes, mark no-show"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* If a meeting was completed but the workflow step needs a UI hint */}
      {cur?.status === "attended" && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-start gap-2">
          <MessageSquareWarning className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-green-700 font-body">
            Meeting attended on {fmt(cur.confirmed_at)}. Step has been advanced.
          </p>
        </div>
      )}
    </div>
  );
}
